import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Hourglass, ChevronRight, AlertCircle, CalendarPlus, Mail, Check, Loader2, ChevronDown, Users, SlidersHorizontal } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import MeetingEmailDialog from '../employees/MeetingEmailDialog';
import { toast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

// Returns a tone for the "days remaining" badge — red for <14 days, amber
// for <45, otherwise emerald. Helps HR triage who needs an action soon.
const toneFor = (days) => {
  if (days < 14) return 'bg-rose-100 text-rose-700 border-rose-200';
  if (days < 45) return 'bg-amber-100 text-amber-700 border-amber-200';
  return 'bg-emerald-100 text-emerald-700 border-emerald-200';
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch {
    return iso;
  }
};

const ActiveProbationCard = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState({}); // { `${empId}:${action}`: true }
  const [meetingFor, setMeetingFor] = useState(null); // employee row for the meeting dialog
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      // Probation length is fixed at 6 months (180 days) server-side, matching
      // the employee-card badge — no client window to shorten it.
      const r = await axios.get(`${API}/api/dashboard/active-probation`, { headers });
      setRows(r.data?.rows || []);
      setTotal(r.data?.total || 0);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const runAction = async (row, action) => {
    const key = `${row.id}:${action}`;
    setBusy(b => ({ ...b, [key]: true }));
    try {
      const url = action === 'appraisal'
        ? `${API}/api/dashboard/probation/${row.id}/schedule-appraisal`
        : `${API}/api/dashboard/probation/${row.id}/send-confirmation-email`;
      const r = await axios.post(url, {}, { headers });
      toast({
        title: action === 'appraisal' ? 'Appraisal scheduled' : 'Email sent',
        description: r.data?.message || 'Done.',
      });
      // Optimistically mark the action as taken so the ✓ shows immediately.
      const stamp = new Date().toISOString();
      setRows(rs => rs.map(rr => rr.id === row.id ? {
        ...rr,
        actions: {
          ...(rr.actions || {}),
          [action === 'appraisal' ? 'appraisal_scheduled' : 'confirmation_email_sent']: stamp,
        },
      } : rr));
    } catch (err) {
      toast({
        title: action === 'appraisal' ? 'Could not schedule' : 'Could not send email',
        description: err?.response?.data?.detail || err?.message || 'Please retry.',
        variant: 'destructive',
      });
    } finally {
      setBusy(b => { const c = { ...b }; delete c[key]; return c; });
    }
  };

  return (
    <Card className="border-0 shadow-sm" data-testid="active-probation-card">
      <CardHeader className="pb-2 flex flex-row items-center justify-between gap-3 flex-wrap">
        <CardTitle className="text-base font-semibold text-gray-800 flex items-center gap-2">
          <Hourglass className="h-5 w-5 text-indigo-600" />
          Active Probation Staff
          {!loading && (
            <Badge variant="outline" className="text-[10px] ml-1">{total}</Badge>
          )}
        </CardTitle>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-500" data-testid="probation-window-label">
            <SlidersHorizontal className="h-3.5 w-3.5 text-gray-400" />
            6-month probation (from join date)
          </span>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-gray-400 py-4 text-center">Loading…</p>
        ) : error ? (
          <div className="flex items-center gap-2 text-sm text-rose-600 py-4">
            <AlertCircle className="h-4 w-4" /> {error}
          </div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-gray-400 py-4 text-center" data-testid="probation-empty">
            No staff currently on probation.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wider text-gray-400 border-b">
                <tr>
                  <th className="text-left py-2 pr-3">Employee</th>
                  <th className="text-left py-2 pr-3">Department</th>
                  <th className="text-left py-2 pr-3">Position</th>
                  <th className="text-left py-2 pr-3">Joined</th>
                  <th className="text-left py-2 pr-3">Probation Ends</th>
                  <th className="text-left py-2 pr-3">Days Left</th>
                  <th className="text-left py-2 pr-3 w-[200px]">Quick Actions</th>
                  <th className="py-2 w-6" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const acts = r.actions || {};
                  const apprDone = Boolean(acts.appraisal_scheduled);
                  const emailDone = Boolean(acts.confirmation_email_sent);
                  const apprBusy = busy[`${r.id}:appraisal`];
                  const emailBusy = busy[`${r.id}:email`];
                  return (
                    <tr
                      key={r.id}
                      className="border-b last:border-0 hover:bg-gray-50"
                      data-testid={`probation-row-${r.employee_code}`}
                    >
                      <td className="py-2 pr-3">
                        <div className="font-medium text-gray-800">{r.name}</div>
                        <div className="text-[11px] text-gray-400">{r.employee_code}</div>
                      </td>
                      <td className="py-2 pr-3 text-gray-600">{r.department || '—'}</td>
                      <td className="py-2 pr-3 text-gray-600">{r.position || '—'}</td>
                      <td className="py-2 pr-3 text-gray-600">{formatDate(r.join_date)}</td>
                      <td className="py-2 pr-3 font-medium text-gray-800">{formatDate(r.probation_end_date)}</td>
                      <td className="py-2 pr-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${toneFor(r.days_remaining)}`}>
                          {r.days_remaining} day{r.days_remaining === 1 ? '' : 's'}
                        </span>
                      </td>
                      <td className="py-2 pr-3">
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => runAction(r, 'appraisal')}
                            disabled={apprBusy}
                            className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border transition-colors ${
                              apprDone
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                : 'bg-white text-indigo-700 border-indigo-200 hover:bg-indigo-50'
                            } disabled:opacity-50`}
                            title={apprDone ? 'Already scheduled — click to re-send reminder' : 'Notify HR to schedule probation appraisal'}
                            data-testid={`probation-schedule-${r.employee_code}`}
                          >
                            {apprBusy
                              ? <Loader2 className="h-3 w-3 animate-spin" />
                              : apprDone ? <Check className="h-3 w-3" /> : <CalendarPlus className="h-3 w-3" />}
                            {apprDone ? 'Scheduled' : 'Schedule'}
                          </button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                disabled={emailBusy}
                                className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border transition-colors ${
                                  emailDone
                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                    : 'bg-white text-rose-700 border-rose-200 hover:bg-rose-50'
                                } disabled:opacity-50`}
                                title="Email options"
                                data-testid={`probation-email-${r.employee_code}`}
                              >
                                {emailBusy
                                  ? <Loader2 className="h-3 w-3 animate-spin" />
                                  : emailDone ? <Check className="h-3 w-3" /> : <Mail className="h-3 w-3" />}
                                {emailDone ? 'Sent' : 'Email'}
                                <ChevronDown className="h-3 w-3" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                              <DropdownMenuItem
                                onClick={() => runAction(r, 'email')}
                                data-testid={`probation-email-hr-${r.employee_code}`}
                              >
                                <Mail className="h-4 w-4 mr-2 text-rose-600" />
                                Email HR (probation decision)
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => setMeetingFor(r)}
                                data-testid={`probation-invite-meeting-${r.employee_code}`}
                              >
                                <Users className="h-4 w-4 mr-2 text-indigo-600" />
                                Invite staff to meeting
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                      <td className="py-2 text-gray-300 cursor-pointer" onClick={() => navigate(`/employees?focus=${encodeURIComponent(r.id)}`)}>
                        <ChevronRight className="h-4 w-4" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      <MeetingEmailDialog
        open={Boolean(meetingFor)}
        onOpenChange={(o) => { if (!o) setMeetingFor(null); }}
        employee={meetingFor}
      />
    </Card>
  );
};

export default ActiveProbationCard;
